<template>
  <!-- 행사 링크(?event=)로 들어온 손님에게: 행사 이름, 어떤 컨셉이든 고를 수 있다는 것, 인원별 1인 값 -->
  <section v-if="view || loading" class="event-banner" :class="{ 'event-banner--ready': view }" :aria-labelledby="view ? 'event-banner-title' : null" :aria-busy="view ? null : 'true'">
    <div v-if="view" class="event-banner__body">
      <img class="event-banner__crown" src="/main_0_v2_crown.png" alt="" aria-hidden="true" />
      <p class="event-banner__name">{{ view.name }}</p>
      <h2 id="event-banner-title" class="event-banner__title"><template v-for="part in copy.title"><span :key="part">{{ part }}</span>{{ copy.gap }}</template></h2>
      <ul class="event-banner__prices" role="list">
        <li v-for="row in view.rows" :key="row.label">
          <span>{{ row.label }}</span>
          <strong>{{ row.price }}</strong>
        </li>
      </ul>
      <p class="event-banner__hint"><template v-for="part in copy.hint"><span :key="part">{{ part }}</span>{{ copy.gap }}</template></p>
    </div>
  </section>
</template>

<script>
const COPY = {
  ko: { gap: " ", event: "", title: ["프리미엄 컨셉", "자유롭게 골라보세요"], hint: ["어떤 컨셉을 골라도", "가격은 같아요"], solo: "솔로", persons: (n, last) => (last ? `${n}명 이상` : `${n}명`), won: (n) => `${n.toLocaleString("ko-KR")}원`, each: (price) => `1인 ${price}` },
  en: { gap: " ", event: "Special event price", title: ["Premium concepts", "Pick any you like"], hint: ["Same price", "whichever concept you pick"], solo: "Solo", persons: (n, last) => (last ? `${n}+ people` : `${n} people`), won: (n) => `₩${n.toLocaleString("en-US")}`, each: (price) => `${price} each` },
  jp: { gap: "", event: "特別イベント価格", title: ["プレミアムコンセプト", "自由にお選びください"], hint: ["どのコンセプトを選んでも", "同じ価格です"], solo: "お一人様", persons: (n, last) => (last ? `${n}名以上` : `${n}名`), won: (n) => `${n.toLocaleString("en-US")}ウォン`, each: (price) => `お一人様 ${price}` },
}

const parseJson = (value, fallback) => {
  if (value && typeof value === "object") return value
  try {
    return JSON.parse(value || "")
  } catch {
    return fallback
  }
}

export default {
  name: "EventPriceBanner",
  data: () => ({ promotion: null, loading: false }),
  computed: {
    lang() {
      return this.$store.state.lang === "jp" ? "jp" : this.$store.state.lang === "en" ? "en" : "ko"
    },
    copy() {
      return COPY[this.lang]
    },
    view() {
      const promotion = this.promotion
      if (!promotion || promotion.conceptId) return null
      const products = parseJson(promotion.products, []).filter((product) => Number(product.price) > 0)
      const copy = this.copy
      const rows = []
      const solos = products.filter((product) => product.type !== "group").map((product) => Number(product.price))
      if (solos.length) rows.push({ label: copy.solo, price: copy.won(Math.min(...solos)) })
      // 그룹은 가장 싼 그룹 상품의 인원별 1인 값을 그대로 보인다
      const group = products
        .filter((product) => product.type === "group")
        .map((product) => ({ product, tiers: (product.groupPricing && product.groupPricing.tiers) || [] }))
        .sort((a, b) => Number(a.product.price) - Number(b.product.price))[0]
      if (group) {
        const tiers = [...group.tiers].sort((a, b) => Number(a.fromPersons) - Number(b.fromPersons))
        tiers.forEach((tier, index) => {
          rows.push({ label: copy.persons(Number(tier.fromPersons), index === tiers.length - 1), price: copy.each(copy.won(Number(tier.price))) })
        })
        if (!tiers.length) rows.push({ label: copy.persons(2, true), price: copy.each(copy.won(Number(group.product.price))) })
      }
      if (!rows.length) return null
      // 행사 이름은 한국어로만 등록돼 있어 영어·일본어 화면에서는 공통 이름을 쓴다
      const name = this.lang === "ko" ? String(promotion.name || "").trim() : copy.event
      return { name, rows }
    },
  },
  async mounted() {
    const code = String(this.$route.query.event || "").trim()
    if (!code) return
    this.loading = true
    try {
      const response = await fetch(`/api/promotion/select/code?code=${encodeURIComponent(code)}`, { cache: "no-store" })
      if (!response.ok) return
      const list = await response.json()
      this.promotion = (Array.isArray(list) ? list : []).find((item) => item && item.code === code) || null
    } catch {
      this.promotion = null
    } finally {
      this.loading = false
    }
  },
}
</script>

<style scoped>
.event-banner {
  margin: 12px 16px 4px;
  padding: 22px 18px 18px;
  border: 1.5px solid transparent;
  border-radius: 22px;
  text-align: center;
  /* 값을 받아 오는 동안에도 자리를 잡아 아래 화면이 밀리지 않게 한다 */
  min-height: 340px;
}
/* 값이 온 뒤에만 칸 모양을 입힌다(행사 코드가 틀린 링크에서 빈 칸이 번쩍이지 않게) */
.event-banner--ready {
  border-color: #ebd6ad;
  background: radial-gradient(circle at 50% 0, #fff 0, #fff3f6 72%);
  box-shadow: 0 1px 1px -0.5px rgba(0, 0, 0, 0.05), 0 3px 3px -1.5px rgba(0, 0, 0, 0.05), 0 6px 6px -3px rgba(0, 0, 0, 0.05), 0 12px 12px -6px rgba(0, 0, 0, 0.05);
}
/* PC에서는 아래 사진·질문 칸과 같은 너비로 맞춘다 */
@media (min-width: 600px) {
  .event-banner {
    max-width: 480px;
    min-height: 320px;
    margin: 12px auto 4px;
  }
  /* 영어·일본어는 제목이 두 줄이라 더 높다 */
  :lang(en) .event-banner,
  :lang(ja) .event-banner {
    min-height: 352px;
  }
}
/* 값을 받아 온 뒤 글이 툭 튀어나오지 않게 살짝 떠오른다 */
.event-banner__body {
  animation: event-banner-in 220ms cubic-bezier(0.23, 1, 0.32, 1);
}
@keyframes event-banner-in {
  from {
    opacity: 0;
    transform: translateY(6px);
  }
}
@keyframes event-banner-fade {
  from {
    opacity: 0;
  }
}
@media (prefers-reduced-motion: reduce) {
  .event-banner__body {
    animation: event-banner-fade 200ms ease;
  }
}
.event-banner__crown {
  display: block;
  width: 36px;
  height: auto;
  margin: 0 auto 6px;
}
.event-banner__name {
  margin: 0;
  color: #b9345d;
  font-size: 13px;
  font-weight: 800;
  letter-spacing: 0.02em;
}
.event-banner__title {
  margin: 6px 0 14px;
  color: #2d2326;
  font-family: "Cafe24Danjunghae", serif;
  font-size: clamp(22px, 6.4vw, 28px);
  font-weight: 400;
  line-height: 1.25;
  word-break: keep-all;
}
/* 뜻 덩어리마다 줄이 바뀌게: 한 낱말만 다음 줄에 떨어지지 않는다 */
.event-banner__title span,
.event-banner__hint span {
  display: inline-block;
  max-width: 100%;
}
.event-banner__prices {
  display: grid;
  gap: 6px;
  max-width: 340px;
  margin: 0 auto;
  padding: 12px 16px;
  border-radius: 16px;
  background: #fff;
  list-style: none;
}
.event-banner__prices li {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  font-size: 14px;
}
.event-banner__prices span {
  color: #6f6165;
  white-space: nowrap;
}
.event-banner__prices strong {
  color: #b9345d;
  font-size: 16px;
  font-weight: 800;
  white-space: nowrap;
}
.event-banner__hint {
  margin: 12px 0 0;
  color: #6f6165;
  font-size: 13px;
  line-height: 1.5;
  word-break: keep-all;
}
/* 일본어 제목은 한 덩어리가 길어 작은 휴대폰(320)에서도 한 줄에 들어가게 줄인다 */
:lang(ja) .event-banner__title {
  font-size: clamp(19px, 5.6vw, 26px);
  font-weight: 700;
}
:lang(ja) .event-banner__title,
:lang(ja) .event-banner__hint {
  word-break: auto-phrase;
  font-family: "Noto Sans JP", sans-serif;
}
</style>
